import React from 'react';
import Breadcrumbs from '@grnhse/seedling/lib/birch/components/breadcrumbs';
import { SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grnhse/seedling/lib/birch/components/tabs';
import { GlobalWhiteBackgroundImportant } from 'redesign_2022/global_white_background';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  type UserContextValue,
  UserContextProvider,
  useUserContext,
} from 'talent_matching/contexts/user_context';
import { type TalentMatchingTabContextValue } from 'talent_matching/contexts/talent_matching_tab_context';
import { BreakpointContextProvider } from 'talent_matching/contexts/breakpoint_context';
import { useDocumentTitle } from 'shared/utils/react_router_helper';
import { TalentMatchingFilters, RediscoveryProps } from 'talent_matching/types';
import TalentMatching from 'talent_matching/pages/talent_matching';
import TalentRediscovery from 'talent_matching/pages/talent_rediscovery';
import TalentMatchingQueryClientProvider from 'talent_matching/talent_matching_query_client';

const t = withLocalePrefix('talent_matching');

type Props = {
  userContextProps: Omit<UserContextValue, 'activeTab' | 'setActiveTab'>;
  talentMatchingProps: Omit<
    TalentMatchingTabContextValue,
    'activeSidePanelTab' | 'setActiveSidePanelTab' | 'setSavedSearchOptions'
  > & {
    initialFilters: Partial<TalentMatchingFilters>;
    previouslyCalibrated: boolean;
    pollRankingOnLoad: boolean;
    pollIndexingOnLoad: boolean;
  };
  showRediscoveryTab: boolean;
  rediscoveryProps?: RediscoveryProps;
};

const TalentMatchingPageContent = ({
  talentMatchingProps,
  showRediscoveryTab,
  rediscoveryProps,
}: Props) => {
  const { activeTab, setActiveTab } = useUserContext();

  if (showRediscoveryTab && rediscoveryProps) {
    return (
      <Tabs
        index={activeTab}
        onChange={setActiveTab}
        data-provides="talent-matching-tabs"
      >
        <TabList>
          <Tab data-provides="talent-matching-app-review-tab">
            {t('tabs.app_review')}
          </Tab>
          <Tab data-provides="talent-matching-rediscovery-tab">
            {t('tabs.rediscovery')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel key="app-review">
            <TalentMatching talentMatchingProps={talentMatchingProps} />
          </TabPanel>
          <TabPanel key="rediscovery">
            <TalentRediscovery rediscoveryProps={rediscoveryProps} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    );
  }

  return <TalentMatching talentMatchingProps={talentMatchingProps} />;
};

const TalentMatchingPage = (props: Props) => {
  useDocumentTitle(t('document_title'));
  const { talentMatchingProps, userContextProps } = props;
  const { paths, jobTitle } = talentMatchingProps;

  return (
    <BreakpointContextProvider>
      <GlobalWhiteBackgroundImportant />
      <UserContextProvider value={userContextProps}>
        <TalentMatchingQueryClientProvider>
          <Stack spacing={1}>
            <Breadcrumbs
              current={t('title')}
              crumbs={[
                {
                  label: t('breadcrumbs.all_jobs'),
                  href: paths.allJobsPath,
                },
                {
                  label: jobTitle,
                  href: paths.jobDashboardPath,
                },
              ]}
            />
            <SectionHeader large>{t('header')}</SectionHeader>
            <TalentMatchingPageContent {...props} />
          </Stack>
        </TalentMatchingQueryClientProvider>
      </UserContextProvider>
    </BreakpointContextProvider>
  );
};

export default TalentMatchingPage;
